import type {
  TranscriptionSegmentTimed,
  TranscriptionTimedPayload,
  TranscriptionWordTimed,
} from "../types";

type MaybeTimed = TranscriptionTimedPayload | undefined;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function isTimedTranscriptPayload(
  value: unknown,
): value is TranscriptionTimedPayload {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  if (candidate.format !== "segments_v1") return false;
  if (!Array.isArray(candidate.segments)) return false;

  return candidate.segments.every((segment) => {
    if (!segment || typeof segment !== "object") return false;
    const s = segment as Record<string, unknown>;
    return (
      typeof s.text === "string" &&
      isFiniteNumber(s.start_sec) &&
      isFiniteNumber(s.end_sec)
    );
  });
}

export function getTimedTranscriptFromUnknown(value: unknown): MaybeTimed {
  return isTimedTranscriptPayload(value) ? value : undefined;
}

export function buildMarkdownFromTimedTranscript(
  timed: MaybeTimed,
): string | undefined {
  if (!timed?.segments?.length) return undefined;
  const lines = timed.segments
    .map((seg) => {
      const speaker = typeof seg.speaker === "string" ? seg.speaker.trim() : "";
      const text = seg.text?.trim();
      if (!text) return "";
      return speaker ? `**${speaker}** - ${text}` : text;
    })
    .filter(Boolean);
  return lines.length ? lines.join("\n") : undefined;
}

export function resolveTranscriptData(input: {
  transcription?: unknown;
  transcriptionTimed?: unknown;
}): { transcription?: string; transcriptionTimed?: TranscriptionTimedPayload } {
  const directTimed = getTimedTranscriptFromUnknown(input.transcriptionTimed);

  const directTranscription =
    typeof input.transcription === "string" ? input.transcription : undefined;

  const transcriptionObj =
    input.transcription && typeof input.transcription === "object"
      ? (input.transcription as Record<string, unknown>)
      : undefined;

  const nestedTranscription =
    transcriptionObj && typeof transcriptionObj.transcription === "string"
      ? transcriptionObj.transcription
      : undefined;

  const timedFromTranscription = getTimedTranscriptFromUnknown(
    input.transcription,
  );
  const nestedTimedFromTranscription = getTimedTranscriptFromUnknown(
    transcriptionObj?.transcription_timed,
  );

  const transcriptionTimed =
    directTimed ?? nestedTimedFromTranscription ?? timedFromTranscription;

  const transcription =
    directTranscription ??
    nestedTranscription ??
    buildMarkdownFromTimedTranscript(transcriptionTimed);

  return {
    ...(transcription ? { transcription } : {}),
    ...(transcriptionTimed ? { transcriptionTimed } : {}),
  };
}

export function fmtTs(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) {
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export function getActiveSegmentIndex(
  timed: TranscriptionTimedPayload | undefined,
  currentTimeSec: number,
): number {
  if (!timed?.segments?.length) return -1;
  const t = Math.max(0, currentTimeSec);
  return timed.segments.findIndex(
    (s) =>
      t >= s.start_sec &&
      t < (s.end_sec > s.start_sec ? s.end_sec : s.start_sec + 0.001),
  );
}

export type TranscriptHighlightPhase = "past" | "current" | "future";

export function getTranscriptHighlightPhase(
  transcriptTimeSec: number,
  start: number,
  end: number,
): TranscriptHighlightPhase {
  const t = Math.max(0, transcriptTimeSec);
  if (t < start) return "future";
  if (t < end) return "current";
  return "past";
}

// Map word timestamps to the segments_v1 timeline (handles segment-relative word times).
export function resolveWordTimeInTranscriptCoords(
  seg: TranscriptionSegmentTimed,
  w: TranscriptionWordTimed,
): { start_sec: number; end_sec: number } {
  const span = Math.max(0, seg.end_sec - seg.start_sec);
  const looksRelative =
    w.end_sec <= span + 0.12 &&
    w.start_sec >= -0.08 &&
    seg.start_sec + w.end_sec <= seg.end_sec + 0.12;
  if (looksRelative) {
    return {
      start_sec: seg.start_sec + w.start_sec,
      end_sec: seg.start_sec + w.end_sec,
    };
  }
  return { start_sec: w.start_sec, end_sec: w.end_sec };
}

export function splitTranscriptSentences(body: string): string[] {
  const t = body.trim();
  if (!t) return [];
  const parts = t.split(/(?<=[.!?])\s+/u).filter(Boolean);
  return parts.length ? parts : [t];
}

export function tokenizeTranscriptWords(body: string): string[] {
  return body.trim().split(/\s+/u).filter(Boolean);
}

// Approximate each part’s [start_sec, end_sec] inside the segment from character weights.
export function rangesByCharWeight(
  parts: string[],
  segStart: number,
  segEnd: number,
): Array<{ start_sec: number; end_sec: number }> {
  if (!parts.length) return [];
  const lens = parts.map((s) =>
    Math.max(1, s.replace(/\s+/g, " ").trim().length),
  );
  const total = lens.reduce((a, b) => a + b, 0);
  const dur = Math.max(0.02, segEnd - segStart);
  let acc = 0;
  return parts.map((_, i) => {
    const start_sec = segStart + (acc / total) * dur;
    acc += lens[i];
    const end_sec = segStart + (acc / total) * dur;
    return { start_sec, end_sec };
  });
}

// Approximate each sentence's window inside [segStart, segEnd] from character weights.
export function sentenceRangesByCharWeight(
  sentences: string[],
  segStart: number,
  segEnd: number,
): Array<{ start_sec: number; end_sec: number }> {
  return rangesByCharWeight(sentences, segStart, segEnd);
}

// Frontend-only: spread segment duration across words (no per-word times from API).
export function wordRangesByCharWeight(
  words: string[],
  segStart: number,
  segEnd: number,
): Array<{ start_sec: number; end_sec: number }> {
  return rangesByCharWeight(words, segStart, segEnd);
}

/**
 * Absolute speech-rate word windows (live karaoke).
 * Unlike wordRangesByCharWeight, growing/partial text only appends later
 * windows — already-spoken words keep their start times (no rewind).
 */
export function wordRangesBySpeechRate(
  words: string[],
  charsPerSec: number,
): Array<{ start_sec: number; end_sec: number }> {
  if (!words.length) return [];
  const rate = Math.max(1, charsPerSec);
  let t = 0;
  return words.map((word, i) => {
    const chars = Math.max(1, word.replace(/\s+/g, "").length);
    // Count an inter-word gap except after the last token.
    const gap = i < words.length - 1 ? 1 : 0;
    const start_sec = t;
    t += (chars + gap) / rate;
    return { start_sec, end_sec: t };
  });
}

export function getTranscriptTimeOffsetSec(
  timed: TranscriptionTimedPayload | undefined,
  durationSec: number,
): number {
  if (!timed?.segments?.length) return 0;

  const payload = timed as unknown as Record<string, unknown>;
  const directOffsetCandidates = [
    payload.offset_sec,
    payload.start_offset_sec,
    payload.dialogue_start_sec,
    payload.leading_silence_sec,
  ];
  for (const c of directOffsetCandidates) {
    if (typeof c === "number" && Number.isFinite(c) && c >= 0) return c;
  }

  const first = timed.segments[0];
  const last = timed.segments[timed.segments.length - 1];
  const extra = durationSec - Math.max(0, last.end_sec);
  if (first.start_sec === 0 && extra >= 3 && extra <= 12) return extra;

  return 0;
}

export function normalizeSpeakerName(raw?: string): string {
  const value = (raw || "").trim();
  if (!value) return "Ahmed";
  const withoutDoctor = value.replace(/^dr\.?\s+/i, "").trim();
  if (/^person\d+$/i.test(withoutDoctor)) return "Ahmed";
  return withoutDoctor;
}

//Best-effort live HCP name for video transcript labels.
export function extractLiveDoctorName(
  windowTitle?: string | null,
  personaText?: string | null,
): string | null {
  if (windowTitle) {
    const fromTitle = windowTitle.match(
      /\bDr\.?\s+([A-Za-z][A-Za-z'-]+(?:\s+[A-Za-z][A-Za-z'-]+)?)/i,
    );
    if (fromTitle?.[1]) {
      return normalizeSpeakerName(`Dr. ${fromTitle[1]}`);
    }
  }

  if (personaText) {
    const cleaned = normalizeSpeakerName(personaText);
    if (!cleaned) return null;
    if (/^the\s+/i.test(cleaned)) return null;
    if (
      /builder|skeptic|guardian|champion|researcher|persona|relationship/i.test(
        cleaned,
      )
    ) {
      return null;
    }
    // Prefer first name for compact sidebar labels (e.g. Ibrahim).
    return cleaned.split(/\s+/)[0] || null;
  }

  return null;
}

export function inferSpeakerFromSegmentText(text?: string): string | undefined {
  const value = (text || "").trim();
  if (!value) return undefined;
  const m = value.match(/^([A-Za-z][A-Za-z\s'-]{0,40}):/);
  return m?.[1]?.trim();
}
